import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

// Solo cambia el contador visible. No modifica artículos, precios ni descuentos.
const protectionProductId = '15448017797429';
const protectionVariantId = '67896371773749';
let pendingCountRequest = null;

function isPackageProtection(item) {
  const handle = item.handle || item.product?.handle || (item.url || '').split('/products/')[1]?.split(/[?#]/)[0];
  return handle === 'proteccion-de-paquete' ||
    String(item.product_id || '') === protectionProductId ||
    String(item.variant_id || item.id || '') === protectionVariantId;
}

function visibleCartCount(cart) {
  if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') return null;
  return cart.items.reduce((count, item) => isPackageProtection(item) ? count : count + Number(item.quantity || 0), 0);
}

function countFromSections(sections) {
  if (!sections || typeof sections !== 'object') return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || !html.includes('data-rc-visible-count')) continue;
    const page = new DOMParser().parseFromString(html, 'text/html');
    const bubble = page.querySelector('[ref="cartBubble"][data-rc-visible-count]');
    if (!bubble) continue;
    const count = Number(bubble.dataset.rcVisibleCount);
    if (Number.isInteger(count) && count >= 0) return count;
  }
  return null;
}

function requestVisibleCount() {
  if (pendingCountRequest) return pendingCountRequest;
  const root = window.Shopify?.routes?.root || '/';
  pendingCountRequest = fetch(root + 'cart.js', {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  }).then(async (response) => {
    if (!response.ok) throw new Error('No se pudo comprobar el contador del carrito.');
    const count = visibleCartCount(await response.json());
    if (count === null) throw new Error('No se recibió un carrito completo.');
    return count;
  }).finally(() => { pendingCountRequest = null; });
  return pendingCountRequest;
}

/** Contador del icono: unidades del carrito excepto Protección de Paquete. */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  countRevision = 0;

  get currentCartCount() {
    return Number(this.refs.cartBubble.dataset.rcVisibleCount || 0);
  }

  set currentCartCount(value) {
    this.refs.cartBubble.dataset.rcVisibleCount = String(value);
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    // El valor inicial viene ya filtrado de cart-bubble.liquid.
    // No recuperamos el antiguo cart-count que incluía la protección.
    const initial = Number(this.refs.cartBubble.dataset.rcVisibleCount);
    if (Number.isInteger(initial) && initial >= 0) this.renderCartBubble(initial, false, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.countRevision++;
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow = async (event) => {
    if (!event.persisted) return;
    const revision = ++this.countRevision;
    try {
      const count = await requestVisibleCount();
      if (this.isConnected && revision === this.countRevision) this.renderCartBubble(count, false, false);
    } catch (error) {
      console.error('Contador del carrito:', error);
    }
  };

  onCartUpdate = async (event) => {
    const revision = ++this.countRevision;
    const detail = event.detail || {};
    let count = visibleCartCount(detail.resource);
    if (count === null) count = countFromSections(detail.data?.sections);
    try {
      // Un evento de añadir producto puede contener solo los artículos añadidos.
      // Nunca confundimos esa respuesta parcial con todo el carrito.
      if (count === null) count = await requestVisibleCount();
      if (this.isConnected && revision === this.countRevision) this.renderCartBubble(count, false);
    } catch (error) {
      console.error('Contador del carrito:', error);
    }
  };

  renderCartBubble = async (itemCount, comingFromProductForm = false, animate = true) => {
    const count = Math.max(0, Math.trunc(Number(itemCount) || 0));
    const changed = this.currentCartCount !== count;
    this.currentCartCount = count;
    this.refs.cartBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', count === 0);
    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
    if (!animate || !changed || count === 0) return;
    const revision = this.countRevision;
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (!this.isConnected || revision !== this.countRevision) return;
    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    if (revision === this.countRevision) this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}